import { Skeleton } from '@dnb/eufemia';
import { Form } from '@dnb/eufemia/extensions/forms';
import { useRef, useState } from 'react';
import { useOutletContext } from 'react-router-dom';

import { updateVisibilityConfig } from '@/api/api';
import { useBackend } from '@/hooks/useBackend';
import { useUser } from '@/hooks/useUser';
import { ApiVisibilityRequest, OrganizationVisibility, TeamVisibility } from '@/openapi/schema';
import { EditContainer } from '@/pages/profile/apps/application/ApiProviderPage/VisibilityAndAccessManagementTab/VisibilityManagementSection/EditContainer';
import { ViewContainer } from '@/pages/profile/apps/application/ApiProviderPage/VisibilityAndAccessManagementTab/VisibilityManagementSection/ViewContainer';
import { SwitchContainerMode } from '@/types/SwitchContainerModeTypes';
import { apiOrEvent } from '@/utils/integrationServiceUtils';

import { ApiProviderPageOutletContext } from '../..';

type VisibilityFormData = {
  organizations: OrganizationVisibility[];
  teams: TeamVisibility[];
  isPublic: boolean;
};

export function VisibilityManagementSection() {
  const { api, isTeamAdmin } = useOutletContext<ApiProviderPageOutletContext>();
  const { user } = useUser();

  const dnbOrganization = user && { name: user.organizationName ?? '', id: user.organizationId ?? '' };

  const {
    data: visibilityConfig,
    isValidating: isLoading,
    mutate,
  } = useBackend('/apis/{apiSlug}/visibility', {
    params: { path: { apiSlug: api.slug } },
  });

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const switchModeRef = useRef<SwitchContainerMode>(undefined);

  const handleSave = async (data: VisibilityFormData) => {
    const requestBody: ApiVisibilityRequest = {
      organizationIds: data.organizations.map((org) => org.id),
      teamIds: data.teams.map((team) => team.id),
    };
    const { response, error } = await updateVisibilityConfig(api.slug, requestBody);

    if (response.ok) {
      setErrorMessage(null);
      void mutate(data, false);
      switchModeRef.current?.('view');
    }

    if (!error || !visibilityConfig) {
      setErrorMessage('Failed to update visibility settings');
      return;
    }

    const consumerAppMessage = `one or more consumer apps with access to this ${apiOrEvent(api.type)}`;
    switch (error.type) {
      case 'https://developer.dnb.no/error-registry/developer-portal/constraints/team-without-api-visibility': {
        const team = error.team;
        const teamOrganization = error.team.organization;
        const isTeamRemoved = visibilityConfig.teams.some((t) => t.id === team.id);
        const isOrganizationRemoved = visibilityConfig.organizations.some((org) => org.id === teamOrganization.id);
        if (isTeamRemoved && isOrganizationRemoved) {
          setErrorMessage(
            `Cannot remove both team ${team.name} (teamId: ${team.id}) and its organization ${teamOrganization.name} (organizationId: ${teamOrganization.id}) from the visibility settings as the team has ${consumerAppMessage}. One of them must be kept.`,
          );
        } else if (isTeamRemoved) {
          setErrorMessage(
            `Cannot remove team ${team.name} (teamId: ${team.id}) from the visibility settings as it has ${consumerAppMessage}. Either keep the team or include its organization ${teamOrganization.name} (organizationId: ${teamOrganization.id}) instead.`,
          );
        } else if (isOrganizationRemoved) {
          setErrorMessage(
            `Cannot remove organization ${teamOrganization.name} (organizationId: ${teamOrganization.id}) from the visibility settings as it has a team ${team.name} (teamId: ${team.id}) with ${consumerAppMessage}. Either keep the organization or include the team instead.`,
          );
        } else {
          setErrorMessage(
            `Team ${team.name} (teamId: ${team.id}) or its organization ${teamOrganization.name} (organizationId: ${teamOrganization.id}) must be included in the visibility settings as the team has ${consumerAppMessage}.`,
          );
        }
        break;
      }
      case 'https://developer.dnb.no/error-registry/developer-portal/constraints/user-without-api-visibility': {
        const user = error.user;
        const userOrganization = user.organization;
        if (userOrganization) {
          const isOrganizationRemoved = visibilityConfig.organizations.some((org) => org.id === userOrganization.id);
          if (isOrganizationRemoved) {
            setErrorMessage(
              `Cannot remove organization ${userOrganization.name} (organizationId: ${userOrganization.id}) from the visibility settings as it has a user ${user.email} with ${consumerAppMessage}.`,
            );
          } else {
            setErrorMessage(
              `Organization ${userOrganization.name} (organizationId: ${userOrganization.id}) must be included in the visibility settings as it has a user ${user.email} with ${consumerAppMessage}.`,
            );
          }
        } else {
          setErrorMessage(
            `User ${user.email} has ${consumerAppMessage}, but no organization. Reach out to the Developer Portal team for assistance as this is a data inconsistency.`,
          );
        }
        break;
      }
      default: {
        setErrorMessage(error.detail ?? 'Failed to update visibility settings');
        break;
      }
    }
  };

  return (
    <Skeleton show={isLoading}>
      {visibilityConfig && dnbOrganization && (
        <Form.Handler<VisibilityFormData> defaultData={visibilityConfig} onSubmit={handleSave}>
          <Form.Section>
            <ViewContainer
              apiClassification={api.classification}
              apiType={api.type}
              isPublic={visibilityConfig.isPublic}
              isTeamAdmin={isTeamAdmin}
              organizations={visibilityConfig.organizations}
              setErrorMessage={setErrorMessage}
              switchContainerModeRef={switchModeRef}
              teams={visibilityConfig.teams}
            />
            <EditContainer
              currentConfig={visibilityConfig}
              dnbOrganization={dnbOrganization}
              errorMessage={errorMessage}
              switchContainerModeRef={switchModeRef}
            />
          </Form.Section>
        </Form.Handler>
      )}
    </Skeleton>
  );
}
